import type {
  Alert,
  AlertErrorHandlerFactory,
  AlertFactory,
} from '@/features/shell'
import { VanishedDialogViewModel } from './VanishedDialog/VanishedDialogViewModel'
import { links } from '@/utils/link'
import { type OpportunityStore } from './OpportunityStore'
import type { PickupResult } from '@/features/jobs'
import {
  opportunitySwooping,
  type SwoopingAction,
} from './opportunity-swooping'
import { action, observable, computed } from 'mobx'
import { task } from 'mobx-task'
import { filter, map, tap } from 'rxjs/operators'
import { Id } from '@/infra/id'
import type { NavigateFn } from '@/RootStore'
import { toStream, toTaskResultStream } from '@/utils/mobx-util'
import { filterNonNil } from '@/utils/rx-util'
import type {
  FetchBookedConsultationsFn,
  PickupConsultationJobFn,
} from '@/features/jobs'
import { type OpportunityViewModel } from './OpportunityViewModel'
import { type ConsultationModalViewModel } from '@/features/opportunities/ConsultationModal/ConsultationModalViewModel'
import type { GetCurrentMemberFn } from '@/features/iam'
import { type Opportunity } from './Opportunity'

/**
 * View model for Opportunity Details.
 */
export class OpportunityDetailsViewModel {
  /**
   * The selected opportunity.
   */
  @observable accessor selected: OpportunityViewModel | null = null

  /**
   * View model for the Vanished dialog.
   */
  readonly vanishedDialogVm = new VanishedDialogViewModel()

  constructor(
    private readonly _opportunityStore: OpportunityStore,
    private readonly _fetchBookedConsultations: FetchBookedConsultationsFn,
    private readonly _pickupConsultationJob: PickupConsultationJobFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _navigate: NavigateFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _canPickUpOpportunityAfterMemberDataRefresh: (
      opportunity: Opportunity,
    ) => Promise<boolean>,
    private readonly _consultationModalViewModel: ConsultationModalViewModel,
  ) {
    const selected$ = toStream(() => this.selected)

    // Server rejected with the `Vanished` error when attempting to pick up the
    // opportunity.
    const opVanishedOnPickup$ = toTaskResultStream(this.pickup).pipe(
      filterNonNil(),
      filter((r) => r === 'Vanished'),
    )
    // Opportunity selection changed.
    const selection$ = selected$.pipe(map((vm) => (vm ? vm.opportunity : null)))
    // Deselects the opportunity being viewed if it vanishes (someone else
    // picked it up or it was deleted) and shows the Vanished dialog, for the
    // app's lifetime rather than just while the Ops view is showing.
    opportunitySwooping(
      // Manual trigger stream.
      opVanishedOnPickup$,
      // Op selection change stream.
      selection$,
      // The opportunity events.
      _opportunityStore.events$,
    )
      .pipe(
        tap(
          action((swoopingAction: SwoopingAction) => {
            switch (swoopingAction) {
              case 'OnlyDeselect':
                this.deselect()
                break
              case 'DeselectAndShowVanished':
                this.deselect()
                this.vanishedDialogVm.show()
                break
            }
          }),
        ),
      )
      .subscribe()
  }

  /**
   * The signed-in member.
   */
  @computed
  get currentMember() {
    return this._getCurrentMember()
  }

  /**
   * The selected opportunity's consultation, if any.
   */
  @computed
  get consultation() {
    return this.selected?.opportunity?.consultation
  }

  /**
   * Indicates whether the apply button can be shown.
   */
  @computed
  get canShowApplyButton() {
    return Boolean(this.selected?.opportunity.isDispatchOpportunity)
  }

  /**
   * Indicates whether the actor has applied to the opportunity.
   */
  @computed
  get applied() {
    return Boolean(this.selected?.opportunity.isCandidate)
  }

  /**
   * Whether applying for or withdrawing from the opportunity is pending.
   */
  @computed
  get isApplyPending() {
    return (
      this.applyForOpportunity.pending ||
      this.withdrawOpportunityApplication.pending
    )
  }

  /**
   * Selects or unselects an opportunity.
   */
  @action.bound
  select(opportunityViewModel: OpportunityViewModel | null) {
    this.selected = opportunityViewModel
  }

  /**
   * Deselects the currently selected Opportunity.
   */
  @action.bound
  deselect() {
    this.select(null)
  }

  /**
   * Pick up the selected opportunity. This is the non-dispatch-routing pickup action.
   */
  pickup = task.resolved(() =>
    this._pickup().catch(
      this._createAlertErrorHandler('Picking up the job failed'),
    ),
  )

  /**
   * Applies the candidate for the opportunity.
   */
  applyForOpportunity = task.resolved(() =>
    this._applyForOpportunity().catch(
      this._createAlertErrorHandler('Applying for the job failed'),
    ),
  )

  /**
   * Withdraws the candidate's application from the opportunity.
   */
  withdrawOpportunityApplication = task.resolved(async () => {
    await this._opportunityStore
      .withdrawApplication(this.selected!.opportunity)
      .catch(
        this._createAlertErrorHandler('Withdrawing the application failed'),
      )
  })

  /**
   * Provides error handling for any pickup operation.
   *
   * @param pickup
   */
  private async _handlePickupOperation(pickup: Promise<PickupResult>) {
    const op = this.selected
    if (!op) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Hang tight!', `Trying to snag ${op.opportunity.name}...`)
      .show()

    try {
      const result = await pickup
      this.select(null)
      this._handlePickupResult(result, alert, op)
      return result
    } catch (err) {
      alert.dismiss()
      throw err
    }
  }

  private async _applyForOpportunity() {
    const canPickup = await this._canPickUpOpportunityAfterMemberDataRefresh(
      this.selected!.opportunity,
    )

    if (!canPickup) {
      await this._handlePickupOperation(Promise.resolve('MaxConcurrentJobs'))

      return
    }

    await this._opportunityStore.applyForOpportunity(this.selected!.opportunity)
  }

  /**
   * Picks up the selected opportunity, as a consultation when it is one.
   */
  private async _pickup() {
    const canPickup = await this._canPickUpOpportunityAfterMemberDataRefresh(
      this.selected!.opportunity,
    )
    if (!canPickup) {
      return await this._handlePickupOperation(
        Promise.resolve('MaxConcurrentJobs'),
      )
    }

    // Picking up a consultation.
    if (this.consultation) {
      const bookedConsultations = await this._fetchBookedConsultations()

      if (
        !this.consultation.availability ||
        this.consultation.availability.length === 0
      ) {
        return await this._handlePickupOperation(
          this._pickupConsultationJob(
            this.selected!.opportunity.id!.toString(),
            undefined,
          ),
        )
      }

      const bookedTime =
        await this._consultationModalViewModel.promptForTimeSlots(
          {
            requestedLength: this.consultation.requestedLength,
            availability: this.consultation.availability,
            bookedConsultations,
            client: this.selected!.opportunity.client.user,
          },
          this.currentMember,
        )

      if (!bookedTime) {
        return
      }

      return await this._handlePickupOperation(
        this._pickupConsultationJob(
          this.selected!.opportunity.id!.toString(),
          bookedTime,
        ),
      )
    }

    // Picking up a normal job.
    return await this._handlePickupOperation(
      this._opportunityStore.pickup(this.selected!.opportunity),
    )
  }

  /**
   * Handles the pickup result.
   */
  private _handlePickupResult(
    result: PickupResult,
    alert: Alert,
    op: OpportunityViewModel,
  ) {
    switch (result) {
      case 'Vanished': {
        // The swooping subscription in the constructor shows the Vanished dialog.
        alert.dismiss()
        break
      }
      case 'MaxConcurrentJobs': {
        alert
          .withState('error')
          .saying(
            'Job capacity reached',
            'Close some active jobs to pick up more.',
          )
          .autoDismiss(3000)
        break
      }
      case 'Success': {
        alert
          .withState('success')
          .saying('Woohoo!', 'You got the job!')
          .autoDismiss(3000)

        this._navigate(links.job(Id.assert(op.opportunity.id)))
        break
      }
    }
  }
}
